import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';
import {getNoOfGridItems} from '@Components/base-grid';
import {AiVideoPromptBox} from '@Panels/ai-video-panel/components/ai-video-prompt-box/ai-video-prompt-box';
import {AiVideoEmptyState} from '@Panels/ai-video-panel/components/ai-video-empty-state/ai-video-empty-state';
import {AiVideoFailedGenerationsList} from '@Panels/ai-video-panel/components/ai-video-failed-generations-list';
import {FullscreenVideoPreview} from '@Components/fullscreen-video-preview/fullscreen-video-preview';
import {AiVideoGenerationLimitReached} from '@Panels/ai-video-panel/components/ai-video-generation-limit-reached';
import {GeneratedVideoGrid} from '@Panels/ai-video-panel/components/generated-video-grid/generated-video-grid';
import {GENERATED_VIDEO_GRID_ID} from '@Panels/ai-video-panel/components/generated-video-grid/generated-video-grid.types';
import type {AiVideoBackendData} from '@Libraries/ai-video.library';
import {isUserPremium} from '@Libraries/user.library';
import type {AiVideoViewProps} from './ai-video-view.types';
import {useAppSelector} from '@/hooks';
import {selectHasFailedAiVideo, selectIsAnyAiVideoGenerating} from '@Components/poster-editor/poster-editor-reducer';
import styles from './ai-video-view.module.scss';
import {isMobile} from 'react-device-detect';

export function AiVideoView(props: AiVideoViewProps): ReactElement {
  const numberOfItems = useAppSelector((state) => {
    return getNoOfGridItems(state, GENERATED_VIDEO_GRID_ID);
  });
  const isGenerating = useAppSelector(selectIsAnyAiVideoGenerating);
  const hasFailedGenerationMessage = useAppSelector(selectHasFailedAiVideo);
  const showEmptyState = numberOfItems === 0 && !isGenerating && !hasFailedGenerationMessage;
  const [previewVideo, setPreviewVideo] = useState<AiVideoBackendData | null>(null);
  const [generatedVideos, setGeneratedVideos] = useState<AiVideoBackendData[]>([]);

  const prependGeneratedVideos = useCallback((videos: AiVideoBackendData[]): void => {
    if (videos.length === 0) {
      return;
    }
    setGeneratedVideos((prev) => {
      const existingIds = new Set(prev.map((video) => video.id));
      const additions = videos.filter((video) => !existingIds.has(video.id));
      if (additions.length === 0) {
        return prev;
      }
      return [...additions, ...prev];
    });
  }, []);

  const appendLoadedVideos = useCallback((videos: AiVideoBackendData[]): void => {
    if (videos.length === 0) {
      return;
    }
    setGeneratedVideos((prev) => {
      const existingIds = new Set(prev.map((video) => video.id));
      const additions = videos.filter((video) => !existingIds.has(video.id));
      if (additions.length === 0) {
        return prev;
      }
      return [...prev, ...additions];
    });
  }, []);

  const onFullscreenPreview = useCallback((video: AiVideoBackendData): void => {
    setPreviewVideo(video);
  }, []);

  const getGeneratedAiVideosGrid = (): ReactElement | null => {
    if (!isUserPremium()) {
      return null;
    }
    return (
      <GeneratedVideoGrid
        isFullScreen={props.isFullScreen}
        maxSelectionNumber={props.maxSelectionNumber}
        onAddToDesign={(backendData): void => {
          props.onAddToDesign(backendData);
        }}
        onFullscreenPreview={onFullscreenPreview}
        onVideosLoaded={appendLoadedVideos}
      />
    );
  };

  const getEmptyState = (): ReactElement | null => {
    if (!showEmptyState) {
      return null;
    }
    return (
      <div className={styles.emptyStateOverlay}>
        <AiVideoEmptyState className={'_full-height'} isFullScreen={props.isFullScreen}/>
      </div>
    );
  };

  const getFullVideoPreview = (): ReactElement | null => {
    if (!previewVideo) {
      return null;
    }
    return (
      <FullscreenVideoPreview
        items={generatedVideos}
        currentItemId={previewVideo.id}
        getPreviewData={(video) => ({
          url: video.videoProps.url,
          thumbUrl: video.videoProps.thumbUrl,
          width: video.videoProps.width,
          height: video.videoProps.height,
          duration: video.videoProps.duration,
          hasAudio: video.prompt.has_audio,
        })}
        onClose={(): void => {
          setPreviewVideo(null);
        }}
        onNavigate={(video): void => {
          setPreviewVideo(video);
        }}
        onAddToDesign={(video): void => {
          props.onAddToDesign(video);
          setPreviewVideo(null);
        }}
      />
    );
  };

  return (
    <div className="flex-v-row _full-height _position-relative">
      <AiVideoPromptBox isFullScreen={props.isFullScreen} onFullscreenPreview={onFullscreenPreview} onVideoGenerated={prependGeneratedVideos} />
      {!props.isFullScreen || isMobile ? <AiVideoGenerationLimitReached /> : null}
      <AiVideoFailedGenerationsList />
      {getGeneratedAiVideosGrid()}
      {getEmptyState()}
      {getFullVideoPreview()}
    </div>
  );
}
